import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from "@angular/core";
import { HelperService, UserService, WindowService } from "@mypxplat/xplat/core";
import { SearchClient, searchClient } from "@algolia/client-search";
import { CommunitySearchBaseComponent } from "@mypxplat/xplat/features";

import { AppService, AvoService, FirebaseService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpParams } from "@angular/common/http";
import { Location } from "@angular/common";

function getCreatedTimestamp(created: any): number {
  if (created == null) return 0;
  if (typeof created === "number") {
    // Unix timestamp: seconds (10 digits) or milliseconds (13 digits)
    return created < 1e12 ? created * 1000 : created;
  }
  if (typeof created === "string") return new Date(created).getTime();
  if (typeof created?.toDate === "function") return created.toDate().getTime();
  const seconds = created._seconds ?? created.seconds;
  return seconds != null ? seconds * 1000 : 0;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-community-search",
  templateUrl: "community-search.component.html",
  standalone: false,
})
export class CommunitySearchComponent extends CommunitySearchBaseComponent {
  @Output() viewPost: EventEmitter<any> = new EventEmitter();
  @Output() postsLoaded: EventEmitter<any> = new EventEmitter();
  @Output() scrollToTop: EventEmitter<any> = new EventEmitter();
  @Output() clearSearch: EventEmitter<any> = new EventEmitter();
  @Input() betaStringId: string;
  @Input() facetFilters: string;
  /**
   * Community to search within. Defaults to Fender Studio Pro. Ignored when `betaStringId`
   * or `facetFilters` is set — those paths supply their own scoping.
   */
  @Input() communityStringId: string = "fender-studio-pro";
  private _query: string;
  @Input() set query(value) {
    this._query = value;
  }
  get query() {
    return this._query;
  }
  public algoliaClient: SearchClient;
  public results: Array<any>;
  public parentsMap: any = {};
  public page: number = 1;
  public totalPages: number;
  protected searching = false;

  constructor(
    userService: UserService,
    public helperService: HelperService,
    public communityService: WebCommunityService,
    private _router: Router,
    private _location: Location,
    public appService: AppService,
    private _win: WindowService,
    public avoService: AvoService,
    public fbService: FirebaseService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.algoliaClient = searchClient(this.env.algolia.app_id, this.env.algolia.apiKey);
    if (this.communityService.cachedSearchResults) {
      this.parentsMap = this.communityService.cachedSearchParentsMap;
      this.results = this.communityService.cachedSearchResults;
      this.page = this.communityService.cachedSearchPage;
      this._win.setTimeout(() => {
        this.postsLoaded.next(true);
      }, 300);
    }
    this.search();
  }

  search(args?, scrollToTop?, reset?) {
    this.searching = true;
    if (!args || args.key == "Enter") {
      const params = new HttpParams().appendAll({ search: this.query });
      this._location.replaceState(location.pathname, params.toString());
      if (reset) {
        this.page = 1;
        this.totalPages = undefined;
        this.communityService.cachedSearchResults = undefined;
        this.communityService.cachedSearchParentsMap = undefined;
        this.communityService.cachedSearchPage = undefined;
      }
      if (!this.page) this.page = 1;
      let algoliaArgs: any = {
        highlightPreTag: "<span class='algolia-highlight'>",
        highlightPostTag: "</span>",
        attributesToHighlight: ["message", "subject"],
        page: this.page - 1 < 0 ? 0 : this.page - 1,
      };
      if (this.facetFilters) {
        algoliaArgs.filters = this.facetFilters;
      } else if (this.betaStringId) {
        algoliaArgs.filters = `beta_string_id:"${this.betaStringId}"`;
      } else if (this.communityStringId) {
        algoliaArgs.filters = `community_string_id:"${this.communityStringId}"`;
      }
      this.algoliaClient
        .search({
          requests: [
            {
              indexName: this.betaStringId ? "prs_beta_community_posts_sorted_created_desc" : this.env.communityAlgoliaIndex,
              query: this.query,
              ...algoliaArgs,
            },
          ],
        })
        .then((res) => {
          const result = res?.results[0] as any;
          const { hits, nbPages } = result;
          if (!this.betaStringId) {
            const te = this.avoService.trackEvent();
            if (te.communitySearch) {
              te.communitySearch({
                userId_: this.user.id,
                searchText: this.query,
                searchResultsCount: hits.length,
                prsId: this.user.id,
                siteId: "my_fender",
              });
            }
          }

          this.totalPages = nbPages;
          hits.sort((a: any, b: any) => {
            const aTs = getCreatedTimestamp(a.created);
            const bTs = getCreatedTimestamp(b.created);
            return bTs - aTs; // DESC: newer first
          });
          let parentPromises = [];
          hits.forEach((hit: any) => {
            if (hit.parentID) parentPromises.push(this.communityService.getPostByID(hit.parentID, true, false, this.betaStringId ? "beta_community_posts" : this.env.communityFirestoreCollection));
            if (hit.useful && hit.useful.length) {
              let iMarkedUseful = hit.useful.filter((id) => id == this.userService.user.id).length;
              hit.markedUseful = iMarkedUseful;
            }
          });
          Promise.all(parentPromises).then((parents) => {
            parents.forEach((parent) => {
              if (parent) this.parentsMap[parent.id] = parent;
            });
            this.results = hits.filter((hit: any) => {
              return !hit.deleted && (!hit.parentID || this.parentsMap[hit.parentID]);
            });
            this.communityService.cachedSearchResults = this.results;
            this.communityService.cachedSearchParentsMap = this.parentsMap;
            if (scrollToTop) this.scrollToTop.next(true);
            this.searching = false;
          });
        });
    }
  }

  toggleUseful(post) {
    this.communityService.updatePost(post.objectID, {
      useful: post.markedUseful ? this.fbService.arrayRemove(this.user.id) : this.fbService.arrayUnion(this.user.id),
    });
    post.markedUseful = !post.markedUseful;
  }

  clear() {
    this.clearSearch.next(true);
  }

  paginate(direction) {
    this.page = this.page + (direction == "next" ? 1 : -1);
    this.communityService.cachedSearchPage = this.page;
    this.search(false, true);
  }

  viewPostClicked(args) {
    let id = args.objectID;
    if (args.parentID) id = args.parentID;
    if (this.betaStringId) {
      this._router.navigate([`beta/betapost/${id}`], { queryParams: { betaStringId: this.betaStringId, searchTerm: this.query } });
    } else {
      this._router.navigate(["community/post/" + id]);
    }
  }
}
